import { URL_SCHEMES } from "../../../util/general-helpers";

const URL_PATTERNS = {
  PROTOCOL: /^([a-zA-Z][a-zA-Z0-9+.-]*:)(\/\/)?/,
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^\+?[0-9\- ]+$/,
  FILE: /^file:\/\/(?:localhost)?\/.+$/,
  BLOB: /^blob:[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+$/,
  INVALID_CHARS: /[,\s]/,
  SSH: /^(?:ssh|sftp):\/\/(?:[^@]+@)?[^:/\s]+(?::[0-9]+)?(?:\/.*)?$/,
  IP_ADDRESS: /^(?:\d{1,3}\.){3}\d{1,3}$/,
};

const SCHEMES_REQUIRING_HOSTNAME = [
  "http://",
  "https://",
  "ftp://",
  "ssh://",
  "sftp://",
  "ws://",
  "wss://",
];

export const getIconForProtocol = (protocol) => {
  const iconMap = {
    "ftp://": "DocumentArrowUpRegular",
    "sftp://": "DocumentArrowUpRegular",
    "blob:": "DocumentRegular",
    "file://": "DocumentRegular",
    "mailto:": "MailReadRegular",
    "tel:": "CallRegular",
    "ssh://": "WindowConsoleRegular",
  };

  return iconMap[protocol] || "LinkRegular";
};

export const extractProtocol = (url) => {
  if (!url) return null;
  const match = url.match(URL_PATTERNS.PROTOCOL);
  return match ? match[0] : null;
};

export const stripProtocol = (url) => {
  if (!url) return "";

  for (const scheme of URL_SCHEMES) {
    if (url.startsWith(scheme)) {
      return url.slice(scheme.length);
    }
  }
  
  return url;
};

export const normalizeUrl = (value, defaultProtocol) => {
  if (!value) return "";
  
  const trimmed = value.trim();
  const hasProtocol = extractProtocol(trimmed);
  
  return hasProtocol ? trimmed : `${defaultProtocol}${trimmed}`;
};

const validateIPAddress = (ip) => {
  if (!URL_PATTERNS.IP_ADDRESS.test(ip)) return false;
  
  const octets = ip.split('.');
  if (octets.length !== 4) return false;
  
  return octets.every(octet => {
    const num = parseInt(octet, 10);
    return !isNaN(num) && num >= 0 && num <= 255 && octet === String(num);
  });
};

const validateHostname = (hostname) => {
  if (!hostname || hostname.length > 253) return false;

  if (hostname.endsWith('.') || hostname.startsWith('.')) {
    return false;
  }

  if (hostname.includes('..')) {
    return false;
  }

  if (validateIPAddress(hostname)) return true;

  const parts = hostname.split('.');
  if (parts.length < 2) return false;

  const tld = parts[parts.length - 1];
  if (tld.length < 2) return false;

  return parts.every(part => {
    if (part.length === 0 || part.length > 63) return false;
    if (part.startsWith('-') || part.endsWith('-')) return false;
    return /^[a-zA-Z0-9-]+$/.test(part);
  });
};

const validateEmail = (email) => {
  if (!email || email.length > 320) return false;
  
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  
  const [localPart, domain] = parts;
  
  if (!localPart || localPart.length > 64) return false;
  if (!domain || domain.length > 253) return false;
  
  if (!/^[a-zA-Z0-9._%+-]+$/.test(localPart)) return false;
  
  return validateHostname(domain);
};

const validatePhone = (phone) => {
  if (!phone || phone.length > 20) return false;
  return /^\+?[0-9\- ]+$/.test(phone) && phone.replace(/[^0-9]/g, '').length >= 3;
};

export const validateByScheme = (value, scheme) => {
  const validators = {
    "mailto:": () => validateEmail(value.replace("mailto:", "")),
    "tel:": () => validatePhone(value.replace("tel:", "")),
    "file://": () => URL_PATTERNS.FILE.test(value) && value.length < 2048,
    "blob:": () => URL_PATTERNS.BLOB.test(value) && value.length < 2048,
    "ssh://": () => URL_PATTERNS.SSH.test(value) && value.length < 2048,
    "sftp://": () => URL_PATTERNS.SSH.test(value) && value.length < 2048,
  };

  const validator = validators[scheme];
  if (validator) {
    return validator();
  }

  try {
    if (value.length > 2048) return false;
    
    const parsed = new URL(value);
    
    if (SCHEMES_REQUIRING_HOSTNAME.includes(scheme)) {
      return validateHostname(parsed.hostname);
    }

    return true;
  } catch {
    return false;
  }
};

export const validateUrl = (value, required, protocol) => {
  const trimmed = (value || "").trim();

  if (trimmed === "") {
    return {
      valid: !required,
      error: required ? "URL is required" : "",
    };
  }

  if (trimmed.length > 2048) {
    return {
      valid: false,
      error: "URL is too long (max 2048 characters)",
    };
  }

  if (URL_PATTERNS.INVALID_CHARS.test(trimmed)) {
    return {
      valid: false,
      error: "URL cannot contain spaces or commas",
    };
  }

  const fullUrl = normalizeUrl(trimmed, protocol);
  const scheme = extractProtocol(fullUrl);

  if (!scheme) {
    return { valid: false, error: "Missing protocol" };
  }

  if (!URL_SCHEMES.includes(scheme)) {
    return {
      valid: false,
      error: `Unsupported protocol: ${scheme}`,
    };
  }

  const isValid = validateByScheme(fullUrl, scheme);

  if (!isValid) {
    const protocolName = scheme.replace("://", "").replace(":", "");
    return {
      valid: false,
      error: `Invalid ${protocolName} URL`,
    };
  }

  return { valid: true, error: "" };
};